"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";

type AnimationType = "fade-left" | "fade-right" | "fade-up" | "fade-down";

interface AnimationWrapperProps {
  children: React.ReactNode;
  animation: AnimationType;
  duration?: number;
  delay?: number;
  once?: boolean; // Whether animation triggers only once
}

export default function AnimationWrapper({
  children,
  animation,
  duration = 0.8,
  delay = 0,
  once = true,
}: AnimationWrapperProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once }); // Trigger animation when in view

  // Define animation variants based on the animation type
  const variants = {
    "fade-left": {
      initial: { opacity: 0, x: -100 },
      animate: { opacity: 1, x: 0 },
    },
    "fade-right": {
      initial: { opacity: 0, x: 100 },
      animate: { opacity: 1, x: 0 },
    },
    "fade-up": {
      initial: { opacity: 0, y: 100 },
      animate: { opacity: 1, y: 0 },
    },
    "fade-down": {
      initial: { opacity: 0, y: -100 },
      animate: { opacity: 1, y: 0 },
    },
  };

  const selectedVariant = variants[animation];

  return (
    <motion.div
      ref={ref}
      initial={selectedVariant.initial}
      animate={isInView ? selectedVariant.animate : selectedVariant.initial}
      transition={{ duration, delay }}
    >
      {children}
    </motion.div>
  );
}